// ==========================================================================
// Extended Locomotive Smooth Scroll
// ==========================================================================
/* jshint esnext: true */
import SmoothScroll from './vendors/SmoothScroll'

import { $window, $document, $html, html } from '../utils/environment';
import Scroll, { DEFAULTS, EVENT } from './Scroll';
import { getParents, queryClosestParent } from '../utils/html';
import { lerp } from '../utils/maths'

import debounce from '../utils/debounce';
import VirtualScroll from 'virtual-scroll';
import { isNumeric } from '../utils/is';

const keyCodes = {
    LEFT: 37,
    UP: 38,
    RIGHT: 39,
    DOWN: 40,
    SPACE: 32,
    TAB: 9
};

export default class extends SmoothScroll {
    constructor(options) {
        super(options)
    }

    /**
     * Initialize scrolling animations
     */
    init() {
        // Add class to the document to know if SmoothScroll is initialized (to manage overflow on containers)
        document.documentElement.classList.add('has-smooth-scroll');

        this.instance = new VirtualScroll({
            mouseMultiplier: (navigator.platform.indexOf('Win') > -1) ? 1 : 0.4,
            touchMultiplier: 4,
            firefoxMultiplier: 30,
            useKeyboard: false
        });

        this.inertia = this.inertia * 0.1;

        this.instance.scroll = {
            x: 0,
            y: 0,
            direction: null
        }

        this.instance.delta = {
            x: 0,
            y: 0,
            direction: null
        }

        if(this.getSpeed) {
            this.instance.scroll.speed = 0;
        }

        this.instance.on((e) => {
            if (!this.isTicking && !this.isDraggingScrollBar) {
                requestAnimationFrame(() => {
                    if (!this.isScrolling) {
                        this.isScrolling = true;
                        this.checkScroll();
                        html.classList.add(this.isScrollingClassName);
                    }

                    if(!window.app.preventScroll && !window.app.menuOpen) {
                        this.instance.delta.y -= e.deltaY;
                        this.instance.delta.direction = e.deltaY > 0 ? 'up' : 'down'
                    }

                    if(window.app.preventScroll) {
                        this.instance.delta.direction = e.deltaY > 0 ? 'up' : 'down'
                    }

                    if(this.instance.delta.y < 0) this.instance.delta.y = 0;
                    if(this.instance.delta.y > this.instance.limit) this.instance.delta.y = this.instance.limit;
                });
                this.isTicking = true;
            }
            this.isTicking = false;
        });

        this.setScrollLimit();
        this.initScrollBar();

        this.addSections();
        this.addElements();

        this.events();

        this.preloadImages();

        this.timestamp = Date.now();

        this.checkKey = this.checkKey.bind(this);
        window.addEventListener('keydown', this.checkKey, false);

        this.render();
    }

    /**
     * Reset existing elements and find all animatable elements.
     * Called on page load and any subsequent updates.
     */
    addElements() {
        this.animatedElements = [];
        this.parallaxElements = [];


        for (let y = 0 ; y < this.sections.length; y ++) {

            const elements = this.sections[y].element.querySelectorAll(this.selector);
            const len = elements.length;

            for (let i = 0 ; i < elements.length; i ++) {
                let element = elements[i];
                TweenMax.set(element, { clearProps: 'transform' })
                let elementSpeed = element.getAttribute('data-speed') ? element.getAttribute('data-speed') / 10 : false;
                let elementPosition = element.getAttribute('data-position');
                let elementTarget = element.getAttribute('data-target');
                let elementHorizontal = (typeof element.getAttribute('data-horizontal') === 'string');
                let elementSticky = (typeof element.getAttribute('data-sticky') === 'string');
                let elementStickyTarget = element.getAttribute('data-sticky-target');
                let target = (elementTarget && document.querySelector(elementTarget) && document.querySelector(elementTarget).length) ? document.querySelector(elementTarget) : element;
                let elementOffset;
                // reset transform to get the real offset

                if(!this.sections[y].inView) {
                    elementOffset = parseInt(target.getBoundingClientRect().top - this.getTranslate(this.sections[y].element).y)
                } else {
                    elementOffset = parseInt(target.getBoundingClientRect().top + this.instance.scroll.y);
                }

                let elementLimit = elementOffset + target.offsetHeight;

                let elementViewportOffset = null;
                if(typeof element.getAttribute('data-viewport-offset') === 'string') {
                   elementViewportOffset = element.getAttribute('data-viewport-offset').split(',');
                }

                //Manage callback
                let elementCallbackString = (typeof element.getAttribute('data-callback') === 'string') ? element.getAttribute('data-callback') : null;
                let elementCallback = null;

                if(elementCallbackString != null){
                    let event = elementCallbackString.substr(0, elementCallbackString.indexOf('('));
                    let optionsString = elementCallbackString.substr(elementCallbackString.indexOf('('),elementCallbackString.length - event.length);

                    optionsString = optionsString.replace('(','');
                    optionsString = optionsString.replace(')','');

                    let options = optionsString.split('|');

                    let obj = {};

                    for (var j = 0; j < options.length; j++) {

                        let option = options[j].split(':');
                        option[0] = option[0].replace(' ','');

                        let val;
                        //check if value is a boolean
                        if(option[1] === "true") {
                            val = true;
                        }
                        else if(option[1] === "false") {
                            val = false;
                        }
                        //check if value is numeric
                        else if(/^\d+$/.test(option[1])) {
                            val = parseInt(option[1]);
                        }
                        //check if value is a String
                        else {
                            val = option[1];
                        }
                        obj[option[0]] = val;
                    }

                    elementCallback = {event:event, options:obj};
                }

                // If elements stays visible after scrolling past it
                let elementRepeat = (typeof element.getAttribute('data-repeat') === 'string');

                let elementInViewClass = (typeof element.getAttribute('data-inview-class') === 'string') ? element.getAttribute('data-inview-class') : 'is-show';

                if (!elementTarget && element.getAttribute('data-transform')) {
                    elementOffset -= parseInt(JSON.parse(element.getAttribute('data-transform')).y);
                    elementLimit = elementOffset + target.offsetHeight;
                }

                if (elementSticky) {
                    if (!elementStickyTarget || typeof elementStickyTarget === 'undefined' || elementStickyTarget === null) {
                        elementLimit = Infinity;
                    } else {
                        if(document.querySelectorAll(elementStickyTarget).length) {
                            let elementStickyTargetSection = queryClosestParent(document.querySelector(elementStickyTarget), '.js-section')

                            if(elementStickyTargetSection) {
                                let section = this.sections.find(candidate => candidate.element == elementStickyTargetSection)

                                if(!section.inView) {
                                    elementLimit = document.querySelector(elementStickyTarget).getBoundingClientRect().top - element.offsetHeight - this.getTranslate(this.sections[y].element).y
                                } else {
                                    elementLimit = document.querySelector(elementStickyTarget).getBoundingClientRect().top - element.offsetHeight + this.instance.scroll.y;
                                }
                            } else {
                                elementLimit = document.querySelector(elementStickyTarget).getBoundingClientRect().top - element.offsetHeight + this.instance.scroll.y;
                            }
                        } else {
                            // elementLimit = document.querySelector(elementStickyTarget).getBoundingClientRect().top - element.offsetHeight + this.instance.scroll.y;
                        }
                    }
                }

                const newElement = {
                    element,
                    inViewClass: elementInViewClass,
                    limit: elementLimit,
                    offset: Math.round(elementOffset),
                    repeat: elementRepeat,
                    callback: elementCallback,
                    viewportOffset: elementViewportOffset
                };


                // For parallax animated elements
                if (elementSpeed !== false) {
                    let elementPosition = element.getAttribute('data-position');
                    let elementHorizontal = (typeof element.getAttribute('data-horizontal') === 'string');
                    let elementMiddle = ((elementLimit - elementOffset) / 2) + elementOffset;
                    let elementDelay = element.getAttribute('data-delay');

                    newElement.horizontal = elementHorizontal;
                    newElement.middle = elementMiddle;
                    newElement.offset = elementOffset;
                    newElement.position = elementPosition;
                    newElement.speed = elementSpeed
                    newElement.delay = elementDelay;

                    this.parallaxElements.push(newElement);

                } else {
                    newElement.sticky = elementSticky;

                    this.animatedElements.push(newElement);

                    if (elementSticky) {
                        //launch the toggle function to set the position of the sticky element
                        this.toggleElement(newElement);
                    }
                }
            }
        }
    }

    getTranslate(el){
        const translate = {}
        if(!window.getComputedStyle) return;

        const style = getComputedStyle(el);
        const transform = style.transform || style.webkitTransform || style.mozTransform;

        if(transform) {
            let mat = transform.match(/^matrix3d\((.+)\)$/);
            if(mat) return parseFloat(mat[1].split(', ')[13]);
            mat = transform.match(/^matrix\((.+)\)$/);
            translate.x = mat ? parseFloat(mat[1].split(', ')[4]) : 0;
            translate.y = mat ? parseFloat(mat[1].split(', ')[5]) : 0;
        }

        return translate;
    }

    checkKey(e) {

        switch(e.keyCode) {
            case keyCodes.TAB:
                setTimeout(() => {
                    document.documentElement.scrollTop = 0;
                    document.body.scrollTop = 0;
                    if(!(document.activeElement instanceof HTMLBodyElement)){
                        this.scrollTo({ targetElem: document.activeElement, targetOffset: - window.innerHeight / 2});
                    }
                }, 0);
                break;
            case keyCodes.UP:
                if(document.activeElement instanceof HTMLBodyElement){
                    this.instance.delta.y -= 240;
                }
                break;
            case keyCodes.DOWN:
                if(document.activeElement instanceof HTMLBodyElement){
                    this.instance.delta.y += 240;
                }
                break;
            case keyCodes.SPACE:
                if(!(document.activeElement instanceof HTMLInputElement) && !(document.activeElement instanceof HTMLTextAreaElement)) {
                    if(e.shiftKey) {
                        this.instance.delta.y -= window.innerHeight;
                    } else {
                        this.instance.delta.y += window.innerHeight;
                    }
                }
                break;
            default:
                return;
        }

        if(this.instance.delta.y < 0) this.instance.delta.y = 0;
        if(this.instance.delta.y > this.instance.limit) this.instance.delta.y = this.instance.limit;

        this.isScrolling = true;
        this.checkScroll();
    }

    /**
     * Render the class/transform animations, and update the global scroll positionning.
     *
     * @param  {boolean} isForced Determines if this is a forced request (from a manual call to update or a resize)
     * @param  {object}  status      Optional status object received when method is
     *                               called by smooth-scrollbar instance listener.
     * @return {void}
     */
    render(isForced, e) {
        if(this.isScrolling) {
            this.instance.scroll.y = lerp(this.instance.scroll.y,this.instance.delta.y, this.inertia);
        } else if(this.isDraggingScrollBar) {
            this.instance.scroll.y = lerp(this.instance.scroll.y,this.instance.delta.y, 0.2);
        }

        for (let i = this.sections.length - 1; i >= 0; i--) {
            if(this.sections[i].persistent || (this.instance.scroll.y > this.sections[i].offset && this.instance.scroll.y < this.sections[i].limit)) {
                this.transform(this.sections[i].element,0,-this.instance.scroll.y);
                this.sections[i].element.style.visibility = 'visible';
            } else {
                this.sections[i].element.style.visibility = 'hidden';
            }
        }

        if(this.getDirection){
            this.instance.scroll.direction = this.instance.delta.direction

            // if (this.instance.delta.y > this.instance.scroll.y) {
            //     if (this.instance.scroll.direction !== 'down') {
            //         this.instance.scroll.direction = 'down';
            //     }
            // } else if (this.instance.delta.y < this.instance.scroll.y) {
            //     if (this.instance.scroll.direction !== 'up') {
            //         this.instance.scroll.direction = 'up';
            //     }
            // }
        }

        if(this.getSpeed) {
            if (this.instance.delta.y !== this.instance.scroll.y) {
                this.instance.scroll.speed = (this.instance.delta.y - this.instance.scroll.y) / (Date.now() - this.timestamp);
                this.instance.delta.y = this.instance.delta.y;
            }else {
                this.instance.scroll.speed = 0;
            }
        }

        this.transformElements(isForced);
        this.animateElements();

        this.callbacks.onScroll(this.instance);
        this.timestamp = Date.now();

        // scrollbar translation
        let scrollBarTranslation = (this.instance.scroll.y / this.instance.limit) * this.scrollBarLimit;
        this.transform(this.scrollbar,0,scrollBarTranslation);

        this.hasScrollTicking = false;
    }

    destroy() {
        window.removeEventListener('keydown', this.checkKey, false);

        super.destroy()
    }
}
